/**
 * A parentela: quem são os parentes, a que distância, por que caminho e com que confiança.
 *
 * A tela mostra as três coisas que tornam a resposta conferível, e mostra sempre:
 *
 * - **a régua** — saltos e limiar, no cabeçalho. A mesma pessoa tem uma parentela a 1 salto e outra
 *   a 3; uma lista que não diz até onde foi não é resposta, é uma afirmação sem contexto.
 * - **o caminho** — «Ana → mãe → Maria → mãe → Joana». É o que deixa o operador DISCORDAR. Um grau
 *   sem caminho é um número pedindo confiança cega.
 * - **a confiança como PISO** — o elo mais fraco da corrente, nunca o produto nem a média.
 *
 * O grau composto aparece em neutro de propósito: «Avô(ó)» e não «Avô». O caminho não sabe o sexo
 * de quem está no fim dele, e afirmá-lo seria repetir o bug que gravava toda filha como «filho».
 */
import { ChevronRight, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Elo = { de: string; para: string; tipo: string; papel: string; confianca: number };

type ParenteDTO = {
  pessoa_id: string;
  grau: string;
  papel: string;
  saltos: number;
  confianca: number;
  caminho: Elo[];
};

type Resposta = {
  semente: string;
  saltos: number;
  limiar: number;
  truncado: boolean;
  parentes: ParenteDTO[];
  rotulos: Record<string, string>;
};

/** Grau não determinado é dito como tal — e com a distância ao lado, que é a informação que resta. */
function grauLegivel(p: ParenteDTO): string {
  if (p.grau !== 'parente') return p.papel;
  return `Parente · ${p.saltos} passo${p.saltos > 1 ? 's' : ''}`;
}

function Caminho({ p, rotulos }: { p: ParenteDTO; rotulos: Record<string, string> }) {
  return (
    <p className="tipo-corpo-pequeno mt-1 flex flex-wrap items-center gap-1 text-text-tertiary">
      {p.caminho.map((e, i) => (
        <span key={i} className="flex items-center gap-1">
          {i === 0 && <span>{rotulos[`pessoa:${e.de}`] ?? '…'}</span>}
          <ChevronRight className="size-3" />
          <span className="text-text-secondary">{e.papel.toLowerCase()}</span>
          <ChevronRight className="size-3" />
          <span>{rotulos[`pessoa:${e.para}`] ?? '…'}</span>
        </span>
      ))}
    </p>
  );
}

export function PainelParentela({ item }: { item: { id: string } }) {
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [saltos, setSaltos] = useState(2);

  const buscar = useCallback(async () => {
    setCarregando(true);
    const r = await api.get<Resposta>(`/cadastro/pessoas/${item.id}/parentela?saltos=${saltos}`);
    setCarregando(false);
    if (r.ok) setDados(r.dados);
    else toast.error(r.erro);
  }, [item.id, saltos]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Users className="size-4 text-accent" />
          Parentela
        </CardTitle>
        {/* A régua é escolha do operador, e fica à vista: 1 é o laço direto, 2 alcança avô e tio,
            3 alcança primo. Acima disso, numa família grande, a lista deixa de ser «os parentes de
            Fulano» e passa a ser «o sobrenome todo». */}
        <div className="flex items-center gap-1">
          {[1, 2, 3].map((n) => (
            <Chip
              key={n}
              aceso={saltos === n}
              className="cursor-pointer"
              onClick={() => setSaltos(n)}
            >
              {n} salto{n > 1 ? 's' : ''}
            </Chip>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {carregando ? (
          <Spinner />
        ) : !dados || dados.parentes.length === 0 ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nenhum parente a {saltos} salto{saltos > 1 ? 's' : ''}. Só relações de família são
            atravessadas — amigo, colega, sócio e vizinho ficam de fora de propósito: a três passos
            por «vizinho» a parentela seria o bairro.
          </p>
        ) : (
          <>
            <ul>
              {dados.parentes.map((p) => (
                <li
                  key={p.pessoa_id}
                  className="border-b border-border py-2 last:border-0"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <Link
                      to={`/cadastro/pessoas/${p.pessoa_id}`}
                      className="tipo-corpo text-accent hover:underline"
                    >
                      {dados.rotulos[`pessoa:${p.pessoa_id}`] ?? p.pessoa_id}
                    </Link>
                    <span className="tipo-corpo-pequeno shrink-0 text-text-secondary">
                      {grauLegivel(p)}
                    </span>
                  </div>
                  {/* Caminho só a partir de 2 saltos: a um salto ele repetiria o grau. */}
                  {p.saltos > 1 && <Caminho p={p} rotulos={dados.rotulos} />}
                </li>
              ))}
            </ul>
            <p className="tipo-corpo-pequeno mt-2 text-text-tertiary">
              Confiança de cada parente é o <strong>piso</strong> do caminho — o elo mais fraco da
              corrente, nunca o produto nem a média. Régua desta lista: até {dados.saltos} salto
              {dados.saltos > 1 ? 's' : ''}, confiança mínima {dados.limiar}.
              {dados.truncado && ' A lista foi truncada no teto: a parentela é maior que isto.'}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
